import React, { useEffect, useRef, useState } from 'react';

import './LocationGroups.scss';
import { ILocationGroup } from 'interfaces';
import { FiltersService, LocationGroupsService, parseAxiosError } from 'services';
import { useForm} from 'react-hook-form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { BulkDataTextarea, ErrorMessage, FormInput, IBulkDataTextareaAPI } from 'components';
import { Button, ModalHeader, ModalBody, ModalFooter} from '@cfa/react-components';
import { AsyncStatus } from 'enums';

interface ILocationGroupModalProps {
    locationGroup: ILocationGroup,
    editing: boolean,
    onHide: () => void,
}

type ILocationGroupForm = {
    name: string;
    // using a ref for text area location entry
};

export function LocationGroupModal({ locationGroup, editing, onHide }: ILocationGroupModalProps) {
    
    const form = useForm<ILocationGroupForm>({ mode: "onTouched" , defaultValues: locationGroup});
    const locationsTextareaRef = useRef<IBulkDataTextareaAPI>();

    const [loadStatus, setLoadStatus] = useState(AsyncStatus.Initial);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    useEffect(() => {
        if (loadStatus != AsyncStatus.Initial) return;

        locationGroup && locationsTextareaRef.current.setTextarea(locationGroup.locations);
        setLoadStatus(AsyncStatus.Success);
    });
    
    const onCreateLocationGroup = () => {
        if (loadStatus === AsyncStatus.Loading) return;

        const { lines, isValid } = locationsTextareaRef.current.getLinesAndValidate();
        if (!isValid) {
            setErrorMessage('');
            setLoadStatus(AsyncStatus.Error);
            return;
        }

        if (lines.length == 0) {
            setErrorMessage('Need at least one location');
            setLoadStatus(AsyncStatus.Error);
            return;
        }

        form.handleSubmit((formData: ILocationGroupForm) => {

            let payload = {
                name: formData.name,
                locations: lines,
            }

            setErrorMessage('');
            setLoadStatus(AsyncStatus.Loading);
            
            editing && LocationGroupsService.putLocationGroup(locationGroup.id, payload)
                .then(() => {
                    onHide();
                })
                .catch((err) => {
                    setErrorMessage(parseAxiosError(err));
                    setLoadStatus(AsyncStatus.Error);
                });
            
            !editing && LocationGroupsService.postLocationGroup(payload)
                .then(() => {
                    onHide();
                })
                .catch((err) => {
                    setErrorMessage(parseAxiosError(err));
                    setLoadStatus(AsyncStatus.Error);
                });
        })();
    };

    return (<>

        <ModalHeader>
            {editing ? 'Edit Location Group' : 'Create Location Group'}
        </ModalHeader>
        <ModalBody>
            <div>
                {loadStatus === AsyncStatus.Error && (
                    <ErrorMessage message={errorMessage} />
                )}
                <div className="row">
                    <div className="col">
                        <FormInput
                            form={form}
                            label="Name"
                            name="name"
                            registerOptions={{ required: true }}
                        />
                    </div>
                </div>
                <div className="row">
                    <div className="col formItem">
                        <div className="formItem_label">
                            {"Locations"}
                        </div>
                        <div className="targetCountsBadge targetCountsBadge_last targetCountsBadge_bottom">
                            <FontAwesomeIcon icon="info-circle" />&nbsp;
                            Please enter a comma or space separated list of locations. Each location must be a 5 digit number.
                        </div>
                    
                        <BulkDataTextarea
                            itemName="location"
                            itemNamePlural="locations"
                            lineValidRegex={FiltersService.regexLocation}
                            ref={locationsTextareaRef}
                        />
                    </div>
                </div>
            </div>
        </ModalBody>
        <ModalFooter>
            <Button variant="outlined" disabled={loadStatus === AsyncStatus.Loading} onClick={onHide}>
                Cancel
            </Button>
            <Button loading={loadStatus === AsyncStatus.Loading} disabled={!form.formState.isValid} onClick={onCreateLocationGroup}>
                Submit
            </Button>
        </ModalFooter>

    </>);
}
